import { computed, ref, unref, watch, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useStore } from "vuex";
import Unipile from "@/services/unipile/unipile";
import { useContacts } from "@/composables/contacts";
import { useUsers } from "@/composables/organization";
import { usePhoneNumbers } from "@/composables/calls";
import { useCrmConnections } from "@/composables/integrations/useCrmConnections";
import { useBusinessProfile } from "@/composables/organization";
import { GET_INTEGRATIONS } from "@/store/action-types";
import {
  SETTINGS_INTEGRATIONS_ROUTE,
  CONTACTS_ROUTE,
  SETTINGS_ADMIN_ROUTE,
  SETTINGS_CALL_PHONE_NUMBERS_ROUTE,
} from "@/router/routes";
import { ONBOARDING_YOUTUBE_URL } from "@/utils/const";
import {
  isOnboardingYoutubeWatched,
  markOnboardingYoutubeWatched,
} from "@/utils/onboardingChecklistStorage";

const MEETING_PROVIDERS = ["google", "teams", "zoom"];

export interface OnboardingChecklistParams {
  organizationId: string;
  userId?: string;
  isOwner?: boolean;
}

export interface OnboardingStep {
  key: string;
  label: string;
  description: string;
  icon: string;
  completed: boolean;
  routeName: string;
  // Extra query params to land on the right in-page tab (e.g. Admin Settings' tabs are
  // query-driven, not separate routes).
  routeQuery?: Record<string, string>;
  // Present only for steps completed by following an outbound link (e.g. the YouTube
  // step) instead of navigating to an in-app route.
  externalUrl?: string;
  // Shown to owners only, and excluded from completedCount/isComplete so it never
  // blocks the "you're all set" state normal users don't even see this step for.
  optional?: boolean;
}

export function useOnboardingChecklist(
  params: Ref<OnboardingChecklistParams | null> | OnboardingChecklistParams | null
) {
  const organizationId = computed(() => unref(params)?.organizationId ?? null);
  const isOwner = computed(() => unref(params)?.isOwner ?? false);
  const userId = computed(() => unref(params)?.userId ?? "");
  const enabled = computed(() => !!organizationId.value);

  // No backend signal exists for "watched the YouTube channel" — clicking through is
  // the only completion signal, so it's tracked locally per user (see markYoutubeWatched).
  const youtubeWatched = ref(false);
  watch(
    userId,
    (id) => {
      youtubeWatched.value = id ? isOnboardingYoutubeWatched(id) : false;
    },
    { immediate: true }
  );

  const markYoutubeWatched = () => {
    if (!userId.value) return;
    markOnboardingYoutubeWatched(userId.value);
    youtubeWatched.value = true;
  };

  const channelsQuery = useQuery({
    queryKey: ["onboardingConnectedChannels"],
    queryFn: async () => {
      const result = await Unipile.getConnectedChannels();
      if (result.data.status) return result.data.data;
      throw new Error("Failed to load connected channels");
    },
    enabled,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const contactsParams = computed(() =>
    organizationId.value ? { organizationId: organizationId.value, page: 1, limit: 1 } : null
  );
  const contactsQuery = useContacts(contactsParams);

  // hasInvitedTeammate only feeds the owner-only optional "invite a teammate" step, so
  // skip the fetch entirely for everyone else.
  const usersParams = computed(() =>
    isOwner.value && organizationId.value ? { organizationId: organizationId.value } : null
  );
  const usersQuery = useUsers(usersParams);

  const phoneNumbersParams = computed(() =>
    organizationId.value ? { organizationId: organizationId.value, page: 1, limit: 1 } : null
  );
  const phoneNumbersQuery = usePhoneNumbers(phoneNumbersParams);

  // Reuses the app's existing integrations store (also populated by Settings > Integrations)
  // instead of a second, independent fetch of the same connection data.
  const store = useStore();
  const integrationsState = computed(() => store.getters.integrations);
  watch(
    organizationId,
    (orgId) => {
      if (orgId) store.dispatch(GET_INTEGRATIONS, { organizationId: orgId });
    },
    { immediate: true }
  );

  // CRM connections are only relevant for the owner-only "configure CRM" step, so skip the
  // fetch entirely for everyone else.
  const crmOrganizationId = computed(() => (isOwner.value ? organizationId.value : null));
  const crmConnectionsQuery = useCrmConnections(crmOrganizationId);

  // Business profile is owner-only (it's the org's Trust Hub / calling-compliance
  // identity), so skip the fetch entirely for everyone else.
  const businessProfileParams = computed(() =>
    isOwner.value && organizationId.value ? { organizationId: organizationId.value } : null
  );
  const businessProfileQuery = useBusinessProfile(businessProfileParams);

  const hasConnectedChannel = computed(() =>
    (channelsQuery.data.value || []).some(
      (channel: { isConnected: boolean }) => channel.isConnected
    )
  );

  const hasContact = computed(() => (contactsQuery.data.value?.metaData?.totalRecords || 0) > 0);

  const hasInvitedTeammate = computed(() => (usersQuery.data.value?.length || 0) > 1);

  const hasPhoneNumber = computed(() => (phoneNumbersQuery.data.value?.data?.length || 0) > 0);

  const hasConnectedMeetingTool = computed(() =>
    (integrationsState.value?.data || []).some(
      (integration: { provider: string; status?: string }) =>
        MEETING_PROVIDERS.includes(integration.provider) && integration.status !== "error"
    )
  );

  const hasConnectedCrm = computed(() =>
    (crmConnectionsQuery.data.value || []).some(
      (connection: { status: string }) => connection.status === "connected"
    )
  );

  // "Configured" means submitted for verification, not just a saved draft — mirrors
  // hasConnectedCrm above requiring an actual completed connection, not just a visit.
  const hasConfiguredBusinessProfile = computed(
    () => (businessProfileQuery.data.value?.verificationStatus || "not_started") !== "not_started"
  );

  const steps = computed<OnboardingStep[]>(() => {
    const baseSteps: OnboardingStep[] = [
      {
        key: "connect-channel",
        label: "Connect a channel",
        description: "Link your LinkedIn, WhatsApp, or email account to start messaging contacts.",
        icon: "mdi:link-variant",
        completed: hasConnectedChannel.value,
        routeName: SETTINGS_INTEGRATIONS_ROUTE,
      },
      {
        key: "enrich-contacts",
        label: "Enrich your contacts",
        description:
          "Fill in missing details like email, job title, and company info to get richer contact profiles.",
        icon: "mdi:account-search-outline",
        completed: hasContact.value,
        routeName: CONTACTS_ROUTE,
      },
      {
        key: "add-phone-number",
        label: "Add a phone number",
        description:
          "Buy or add a phone number so you can call and text your contacts directly from SalesBuckets.",
        icon: "mdi:phone-plus-outline",
        completed: hasPhoneNumber.value,
        routeName: SETTINGS_CALL_PHONE_NUMBERS_ROUTE,
      },
      {
        key: "connect-meeting-tools",
        label: "Connect your meeting tools",
        description:
          "Link Zoom, Google Meet, or Microsoft Teams to automatically record and transcribe your meetings.",
        icon: "mdi:video-plus-outline",
        completed: hasConnectedMeetingTool.value,
        routeName: SETTINGS_INTEGRATIONS_ROUTE,
      },
      {
        key: "watch-youtube",
        label: "Watch our YouTube channel",
        description: "See quick tutorials and tips on getting the most out of SalesBuckets.",
        icon: "mdi:youtube",
        completed: youtubeWatched.value,
        routeName: "",
        externalUrl: ONBOARDING_YOUTUBE_URL,
      },
    ];

    if (isOwner.value) {
      baseSteps.push({
        key: "configure-crm",
        label: "Configure your CRM integration",
        description:
          "Connect HubSpot or Salesforce to keep contacts, deals, and activity synced automatically.",
        icon: "mdi:cog-sync-outline",
        completed: hasConnectedCrm.value,
        routeName: SETTINGS_INTEGRATIONS_ROUTE,
      });
      baseSteps.push({
        key: "configure-business-profile",
        label: "Configure your business profile",
        description:
          "Register your business identity so your name shows correctly on outbound calls and eligible numbers get spam-label protection.",
        icon: "mdi:card-account-details-outline",
        completed: hasConfiguredBusinessProfile.value,
        routeName: SETTINGS_ADMIN_ROUTE,
        routeQuery: { tab: "Business-Profile" },
      });
    }

    return baseSteps;
  });

  // Invite-a-teammate isn't relevant to non-owners (they can't invite anyone), and for
  // owners it's nice-to-have rather than required, so it's surfaced separately and left
  // out of completedCount/isComplete entirely.
  const optionalSteps = computed<OnboardingStep[]>(() => {
    if (!isOwner.value) return [];
    return [
      {
        key: "invite-teammate",
        label: "Invite a teammate",
        description: "Bring your team in so you can share the workload.",
        icon: "mdi:account-group-outline",
        completed: hasInvitedTeammate.value,
        routeName: SETTINGS_ADMIN_ROUTE,
        optional: true,
      },
    ];
  });

  const isLoading = computed(
    () =>
      channelsQuery.isLoading.value ||
      contactsQuery.isLoading.value ||
      (isOwner.value && usersQuery.isLoading.value) ||
      phoneNumbersQuery.isLoading.value ||
      integrationsState.value?.isLoading ||
      (isOwner.value && crmConnectionsQuery.isLoading.value) ||
      (isOwner.value && businessProfileQuery.isLoading.value)
  );

  const completedCount = computed(() => steps.value.filter((step) => step.completed).length);
  const isComplete = computed(
    () => steps.value.length > 0 && completedCount.value === steps.value.length
  );

  return { steps, optionalSteps, completedCount, isComplete, isLoading, markYoutubeWatched };
}
